/**
 * PIN de 4 cifras de «Pedir la huella al abrir» (Vadik, 20-oct): si la huella
 * falla o se cancela, se abre Rumbo con el PIN. Solo abre la app en este
 * móvil: se guarda aquí (sal + PBKDF2), nunca va al servidor ni sirve para
 * entrar desde otro aparato.
 */
const CLAVE_PIN = 'rumbo-pin';
const CLAVE_FALLOS = 'rumbo-pin-fallos';

export const CIFRAS_PIN = 4;
/** Fallos seguidos antes de la primera espera. */
export const FALLOS_SIN_ESPERA = 5;
const ITERACIONES = 210_000;

type PinGuardado = { sal: string; hash: string };
type Fallos = { fallos: number; hasta: number };

function leer<T>(clave: string): T | null {
  try {
    const valor = window.localStorage.getItem(clave);
    return valor ? (JSON.parse(valor) as T) : null;
  } catch {
    return null;
  }
}

function escribir(clave: string, valor: unknown) {
  try {
    if (valor === null) {
      window.localStorage.removeItem(clave);
    } else {
      window.localStorage.setItem(clave, JSON.stringify(valor));
    }
  } catch {
    // Sin almacenamiento (modo privado): no se puede guardar el PIN.
  }
}

function aBase64(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes));
}

function deBase64(texto: string): Uint8Array<ArrayBuffer> {
  return Uint8Array.from(atob(texto), c => c.charCodeAt(0));
}

async function derivar(pin: string, sal: Uint8Array<ArrayBuffer>) {
  const base = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(pin),
    'PBKDF2',
    false,
    ['deriveBits'],
  );
  const bits = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', salt: sal, iterations: ITERACIONES, hash: 'SHA-256' },
    base,
    256,
  );
  return aBase64(new Uint8Array(bits));
}

export function esPinValido(pin: string): boolean {
  return new RegExp(`^\\d{${CIFRAS_PIN}}$`).test(pin);
}

export function tienePin(): boolean {
  return leer<PinGuardado>(CLAVE_PIN) !== null;
}

export async function guardarPin(pin: string) {
  if (!esPinValido(pin)) {
    throw new Error('PIN no válido');
  }
  const sal = new Uint8Array(16);
  crypto.getRandomValues(sal);
  escribir(CLAVE_PIN, { sal: aBase64(sal), hash: await derivar(pin, sal) });
  escribir(CLAVE_FALLOS, null);
}

export function borrarPin() {
  escribir(CLAVE_PIN, null);
  escribir(CLAVE_FALLOS, null);
}

/**
 * Segundos de espera tras `fallos` seguidos: nada hasta 5, luego 30 s y se
 * dobla en cada fallo, hasta 1 hora.
 */
export function esperaTrasFallos(fallos: number): number {
  if (fallos < FALLOS_SIN_ESPERA) {
    return 0;
  }
  return Math.min(30 * 2 ** (fallos - FALLOS_SIN_ESPERA), 3600);
}

/** Lo que falta de espera; como la del correo, no se reinicia al reabrir. */
export function segundosEsperaPin(ahora = Date.now()): number {
  const f = leer<Fallos>(CLAVE_FALLOS);
  return f ? Math.max(0, Math.ceil((f.hasta - ahora) / 1000)) : 0;
}

export type ResultadoPin = 'ok' | 'incorrecto' | 'esperar';

export async function comprobarPin(
  pin: string,
  ahora = Date.now(),
): Promise<ResultadoPin> {
  const guardado = leer<PinGuardado>(CLAVE_PIN);
  if (!guardado || segundosEsperaPin(ahora) > 0) {
    return 'esperar';
  }
  if ((await derivar(pin, deBase64(guardado.sal))) === guardado.hash) {
    escribir(CLAVE_FALLOS, null);
    return 'ok';
  }
  const fallos = (leer<Fallos>(CLAVE_FALLOS)?.fallos ?? 0) + 1;
  escribir(CLAVE_FALLOS, {
    fallos,
    hasta: ahora + esperaTrasFallos(fallos) * 1000,
  });
  return 'incorrecto';
}
